
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import "./AdminManageNumbers.css";

export default function AdminManageNumbers() {
  // BUSINESS FORM
  const [businessProvider, setBusinessProvider] = useState("Vodacom");
  const [businessFullname, setBusinessFullname] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");

  // NORMAL FORM
  const [normalProvider, setNormalProvider] = useState("Vodacom");
  const [normalFullname, setNormalFullname] = useState("");
  const [normalNumber, setNormalNumber] = useState("");

  // STANDARD FORM
  const [standardFullname, setStandardFullname] = useState("");
  const [standardNumber, setStandardNumber] = useState("");

  // SAVED RECORDS
  const [businessNumbers, setBusinessNumbers] = useState([]);
  const [normalNumbers, setNormalNumbers] = useState([]);
  const [standardNumbers, setStandardNumbers] = useState([]);

  // EDIT FORM
  const [editingItem, setEditingItem] = useState(null);
  const [editProvider, setEditProvider] = useState("Vodacom");
  const [editFullname, setEditFullname] = useState("");
  const [editNumber, setEditNumber] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [loading, setLoading] = useState(true);

  const providers = [
    "Vodacom",
    "Airtel",
    "Tigo",
    "Halotel",
  ];

  // FIRESTORE COLLECTIONS
  const businessRef = collection(db, "depositNumbers");
  const normalRef = collection(db, "normalNumbers");
  const standardRef = collection(db, "othercountry");

  // COMMON STYLES
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: 11,
    marginTop: 10,
    borderRadius: 6,
    border: "1px solid #444",
    background: "#fff",
    color: "#111",
  };

  const buttonStyle = {
    border: "none",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 5,
    cursor: "pointer",
  };

  // LOAD RECORDS
  const loadNumbers = async () => {
    try {
      const [businessData, normalData, standardData] =
        await Promise.all([
          getDocs(businessRef),
          getDocs(normalRef),
          getDocs(standardRef),
        ]);

      setBusinessNumbers(
        businessData.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }))
      );

      setNormalNumbers(
        normalData.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }))
      );

      setStandardNumbers(
        standardData.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        }))
      );
    } catch (error) {
      console.error("Failed to load numbers:", error);
      alert("Failed to load records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNumbers();
    // Load the records when the component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ADD BUSINESS NUMBER
  const handleAddBusiness = async () => {
    if (!businessFullname.trim() || !businessNumber.trim()) {
      alert("Please fill in all business fields.");
      return;
    }

    try {
      await addDoc(businessRef, {
        provider: businessProvider,
        fullname: businessFullname.trim(),
        number: businessNumber.trim(),
      });

      setBusinessFullname("");
      setBusinessNumber("");
      await loadNumbers();

      alert("Business number added successfully.");
    } catch (error) {
      console.error("Add business error:", error);
      alert("Failed to add business number.");
    }
  };

  // ADD NORMAL NUMBER
  const handleAddNormal = async () => {
    if (!normalFullname.trim() || !normalNumber.trim()) {
      alert("Please fill in all phone fields.");
      return;
    }

    try {
      await addDoc(normalRef, {
        provider: normalProvider,
        fullname: normalFullname.trim(),
        number: normalNumber.trim(),
      });

      setNormalFullname("");
      setNormalNumber("");
      await loadNumbers();

      alert("Phone number added successfully.");
    } catch (error) {
      console.error("Add normal number error:", error);
      alert("Failed to add phone number.");
    }
  };

  // ADD STANDARD ACCOUNT
  const handleAddStandard = async () => {
    if (!standardFullname.trim() || !standardNumber.trim()) {
      alert("Please fill in all standard account fields.");
      return;
    }

    try {
      await addDoc(standardRef, {
        name: standardFullname.trim(),
        number: standardNumber.trim(),
      });

      setStandardFullname("");
      setStandardNumber("");
      await loadNumbers();

      alert("Standard account added successfully.");
    } catch (error) {
      console.error("Add standard account error:", error);
      alert("Failed to add standard account.");
    }
  };

  // OPEN EDIT FORM
  const handleEdit = (item, type) => {
    setEditingItem({
      id: item.id,
      type,
    });

    setEditProvider(item.provider || "Vodacom");
    setEditFullname(item.fullname || item.name || "");
    setEditNumber(item.number || "");
  };

  // CANCEL EDIT
  const handleCancelEdit = () => {
    if (savingEdit) return;

    setEditingItem(null);
    setEditProvider("Vodacom");
    setEditFullname("");
    setEditNumber("");
  };

  // SAVE EDIT TO FIRESTORE
  const handleSaveEdit = async () => {
    if (!editingItem) return;

    if (!editFullname.trim() || !editNumber.trim()) {
      alert("Please fill in the name and phone number.");
      return;
    }

    setSavingEdit(true);

    try {
      let collectionName;
      let updatedData;

      if (editingItem.type === "business") {
        collectionName = "depositNumbers";
        updatedData = {
          provider: editProvider,
          fullname: editFullname.trim(),
          number: editNumber.trim(),
        };
      } else if (editingItem.type === "normal") {
        collectionName = "normalNumbers";
        updatedData = {
          provider: editProvider,
          fullname: editFullname.trim(),
          number: editNumber.trim(),
        };
      } else {
        collectionName = "othercountry";
        updatedData = {
          name: editFullname.trim(),
          number: editNumber.trim(),
        };
      }

      await updateDoc(
        doc(db, collectionName, editingItem.id),
        updatedData
      );

      setEditingItem(null);
      setEditProvider("Vodacom");
      setEditFullname("");
      setEditNumber("");

      await loadNumbers();

      alert("Changes saved successfully.");
    } catch (error) {
      console.error("Update record error:", error);
      alert("Failed to save changes. Check your connection and permissions.");
    } finally {
      setSavingEdit(false);
    }
  };

  // DELETE RECORD
  const handleDelete = async (id, type) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this record?"
    );

    if (!confirmed) return;

    const collectionNames = {
      business: "depositNumbers",
      normal: "normalNumbers",
      standard: "othercountry",
    };

    try {
      await deleteDoc(doc(db, collectionNames[type], id));
      await loadNumbers();
      alert("Record deleted successfully.");
    } catch (error) {
      console.error("Delete record error:", error);
      alert("Failed to delete record.");
    }
  };

  // RECORD CARD
  const renderRecord = (item, type) => (
    <div
      key={item.id}
      style={{
        background: "#18181b",
        padding: 12,
        borderRadius: 8,
        marginTop: 8,
        border: "1px solid #303036",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {type !== "standard" && (
          <div style={{ color: "#a5b4fc", fontSize: 12 }}>
            {item.provider || "No provider"}
          </div>
        )}

        <div style={{ fontWeight: "bold", marginTop: 3 }}>
          {item.fullname || item.name || "Unnamed"}
        </div>

        <div style={{ opacity: 0.75, marginTop: 4 }}>
          {item.number}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => handleEdit(item, type)}
          style={{
            ...buttonStyle,
            background: "#2563eb",
          }}
        >
          Edit
        </button>

        <button
          type="button"
          onClick={() => handleDelete(item.id, type)}
          style={{
            ...buttonStyle,
            background: "#dc2626",
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );

  // PROVIDER GROUP
  const renderProviderGroup = (provider, records, type) => {
    const filtered = records.filter(
      (item) => item.provider === provider
    );

    if (filtered.length === 0) return null;

    return (
      <div key={`${type}-${provider}`} style={{ marginTop: 16 }}>
        <h4>{provider}</h4>

        {filtered.map((item) => renderRecord(item, type))}
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0f0f10",
        color: "#fff",
        padding: 20,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1250,
          margin: "0 auto",
        }}
      >
        <h1 style={{ marginBottom: 8 }}>
          Manage Payment Numbers
        </h1>

        <p style={{ color: "#aaa", marginTop: 0 }}>
          Add, edit, save or delete payment account details.
        </p>

        {loading && <p>Loading records...</p>}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
            alignItems: "start",
            marginTop: 24,
          }}
        >
          {/* BUSINESS NUMBERS */}
          <section
            style={{
              background: "#151517",
              padding: 18,
              borderRadius: 10,
              border: "1px solid #303036",
            }}
          >
            <h2>Business Numbers</h2>

            <select
              value={businessProvider}
              onChange={(e) => setBusinessProvider(e.target.value)}
              style={inputStyle}
            >
              {providers.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Full Name"
              value={businessFullname}
              onChange={(e) => setBusinessFullname(e.target.value)}
              style={inputStyle}
            />

            <input
              type="text"
              placeholder="Business Number"
              value={businessNumber}
              onChange={(e) => setBusinessNumber(e.target.value)}
              style={inputStyle}
            />

            <button
              type="button"
              onClick={handleAddBusiness}
              style={{
                ...buttonStyle,
                width: "100%",
                marginTop: 12,
                padding: 12,
                background: "#15803d",
              }}
            >
              Add Business
            </button>

            {providers.map((provider) =>
              renderProviderGroup(
                provider,
                businessNumbers,
                "business"
              )
            )}
          </section>

          {/* NORMAL NUMBERS */}
          <section
            style={{
              background: "#151517",
              padding: 18,
              borderRadius: 10,
              border: "1px solid #303036",
            }}
          >
            <h2>Normal Phones</h2>

            <select
              value={normalProvider}
              onChange={(e) => setNormalProvider(e.target.value)}
              style={inputStyle}
            >
              {providers.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Full Name"
              value={normalFullname}
              onChange={(e) => setNormalFullname(e.target.value)}
              style={inputStyle}
            />

            <input
              type="text"
              placeholder="Phone Number"
              value={normalNumber}
              onChange={(e) => setNormalNumber(e.target.value)}
              style={inputStyle}
            />

            <button
              type="button"
              onClick={handleAddNormal}
              style={{
                ...buttonStyle,
                width: "100%",
                marginTop: 12,
                padding: 12,
                background: "#1d4ed8",
              }}
            >
              Add Phone
            </button>

            {providers.map((provider) =>
              renderProviderGroup(
                provider,
                normalNumbers,
                "normal"
              )
            )}
          </section>

          {/* STANDARD ACCOUNTS */}
          <section
            style={{
              background: "#151517",
              padding: 18,
              borderRadius: 10,
              border: "1px solid #303036",
            }}
          >
            <h2>Standard Accounts</h2>

            <input
              type="text"
              placeholder="Full Name"
              value={standardFullname}
              onChange={(e) => setStandardFullname(e.target.value)}
              style={inputStyle}
            />

            <input
              type="text"
              placeholder="Phone Number"
              value={standardNumber}
              onChange={(e) => setStandardNumber(e.target.value)}
              style={inputStyle}
            />

            <button
              type="button"
              onClick={handleAddStandard}
              style={{
                ...buttonStyle,
                width: "100%",
                marginTop: 12,
                padding: 12,
                background: "#7e22ce",
              }}
            >
              Add Standard
            </button>

            {standardNumbers.map((item) =>
              renderRecord(item, "standard")
            )}
          </section>
        </div>
      </div>

      {/* EDIT MODAL */}
      {editingItem && (
        <div
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelEdit();
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            overflowY: "auto",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-number-title"
            style={{
              width: "100%",
              maxWidth: 460,
              background: "#1b1b1d",
              color: "#fff",
              padding: 24,
              borderRadius: 12,
              boxSizing: "border-box",
              border: "1px solid #3f3f46",
              boxShadow: "0 10px 35px rgba(0,0,0,0.5)",
            }}
          >
            <h2 id="edit-number-title" style={{ marginTop: 0 }}>
              Edit Payment Details
            </h2>

            {editingItem.type !== "standard" && (
              <>
                <label htmlFor="edit-provider">Provider</label>

                <select
                  id="edit-provider"
                  value={editProvider}
                  onChange={(e) => setEditProvider(e.target.value)}
                  style={inputStyle}
                >
                  {providers.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label htmlFor="edit-fullname" style={{ display: "block", marginTop: 14 }}>
              Full Name
            </label>

            <input
              id="edit-fullname"
              type="text"
              value={editFullname}
              onChange={(e) => setEditFullname(e.target.value)}
              style={inputStyle}
              autoFocus
            />

            <label htmlFor="edit-number" style={{ display: "block", marginTop: 14 }}>
              Phone Number
            </label>

            <input
              id="edit-number"
              type="text"
              value={editNumber}
              onChange={(e) => setEditNumber(e.target.value)}
              style={inputStyle}
            />

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 24,
              }}
            >
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={savingEdit}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  padding: 12,
                  background: "#3f3f46",
                  opacity: savingEdit ? 0.6 : 1,
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                style={{
                  ...buttonStyle,
                  flex: 1,
                  padding: 12,
                  background: "#16a34a",
                  opacity: savingEdit ? 0.6 : 1,
                }}
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
